import { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import { ColorBlock } from "./tiptapColorBlock";
import {
  Bold, Italic, List, ListOrdered, Quote, Heading2, Heading3,
  AlignLeft, AlignCenter, AlignRight, ImageIcon, LinkIcon, Loader2, Check,
  Baseline, Highlighter, Eraser,
} from "lucide-react";
import { useUploadImage } from "@/hooks/useFileUpload";
import { useI18n } from "@/lib/i18n";

function ToolbarButton({
  onClick,
  active,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`flex h-7 w-7 items-center justify-center rounded transition-colors disabled:opacity-40 ${
        active ? "bg-foreground text-background" : "text-muted-foreground hover:bg-surface hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  autoFocusStart,
}: {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  /** Place le curseur au tout début du champ dès son affichage (ex. page de création). */
  autoFocusStart?: boolean;
}) {
  const { t } = useI18n();
  const uploadImage = useUploadImage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
      }),
      Link.configure({ openOnClick: false, autolink: true }),
      Image,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder: placeholder || "" }),
      TextStyle,
      Color,
      ColorBlock,
    ],
    content: value,
    autofocus: autoFocusStart ? "start" : false,
    onUpdate: ({ editor }) => {
      setSaveStatus("saving");
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onChange(editor.getHTML());
        setSaveStatus("saved");
      }, 2000);
    },
  });

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Garde le contenu synchronisé si `value` change de l'extérieur (ex. changement de langue)
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;

  const handleImageButtonClick = () => fileInputRef.current?.click();

  const handleLinkButtonClick = () => {
    const previousUrl = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("URL du lien :", previousUrl || "https://");
    if (url === null) return;
    if (url.trim() === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };

  // Encadre le(s) paragraphe(s) sélectionné(s) dans un bloc coloré (fond +
  // padding + coins arrondis) plutôt que de surligner ligne par ligne : si le
  // curseur est déjà dans un bloc coloré, on met juste à jour sa couleur.
  const appliquerBlocColore = (couleur: string) => {
    if (editor.isActive("colorBlock")) {
      editor.chain().focus().updateAttributes("colorBlock", { backgroundColor: couleur }).run();
    } else if (editor.can().wrapIn("colorBlock")) {
      editor.chain().focus().wrapIn("colorBlock", { backgroundColor: couleur }).run();
    }
  };

  const retirerCouleurs = () => {
    const chain = editor.chain().focus().unsetColor();
    if (editor.isActive("colorBlock")) chain.lift("colorBlock");
    chain.run();
  };

  const handleImageSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const result = await uploadImage.mutateAsync(file);
      editor.chain().focus().setImage({ src: result.url }).run();
    } catch {
      // l'échec d'upload n'interrompt pas l'édition — l'utilisateur peut réessayer
    }
  };

  return (
    <div className="rounded-md border border-border bg-surface">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border px-2 py-1.5">
        <ToolbarButton title="H2" active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Heading2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="H3" active={editor.isActive("heading", { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          <Heading3 className="h-4 w-4" />
        </ToolbarButton>
        <span className="mx-1 h-4 w-px bg-border" />
        <ToolbarButton title="Gras" active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Italique" active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <span className="mx-1 h-4 w-px bg-border" />
        <ToolbarButton title="Liste à puces" active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Liste numérotée" active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Citation" active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <Quote className="h-4 w-4" />
        </ToolbarButton>
        <span className="mx-1 h-4 w-px bg-border" />
        <ToolbarButton title="Aligner à gauche" active={editor.isActive({ textAlign: "left" })}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}>
          <AlignLeft className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Centrer" active={editor.isActive({ textAlign: "center" })}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}>
          <AlignCenter className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Aligner à droite" active={editor.isActive({ textAlign: "right" })}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}>
          <AlignRight className="h-4 w-4" />
        </ToolbarButton>
        <span className="mx-1 h-4 w-px bg-border" />
        <ToolbarButton title="Insérer un lien" active={editor.isActive("link")} onClick={handleLinkButtonClick}>
          <LinkIcon className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Insérer une image" disabled={uploadImage.isPending} onClick={handleImageButtonClick}>
          {uploadImage.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
        </ToolbarButton>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelected} />

        <span className="mx-1 h-4 w-px bg-border" />
        <label
          title="Couleur du texte"
          className="relative flex h-7 w-7 cursor-pointer items-center justify-center rounded text-muted-foreground hover:bg-surface hover:text-foreground transition-colors"
        >
          <Baseline className="h-4 w-4" />
          <input
            type="color"
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
          />
        </label>
        <label
          title="Encadrer dans un bloc coloré"
          className={`relative flex h-7 w-7 cursor-pointer items-center justify-center rounded transition-colors ${
            editor.isActive("colorBlock") ? "bg-foreground text-background" : "text-muted-foreground hover:bg-surface hover:text-foreground"
          }`}
        >
          <Highlighter className="h-4 w-4" />
          <input
            type="color"
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            onChange={(e) => appliquerBlocColore(e.target.value)}
          />
        </label>
        <ToolbarButton
          title="Retirer les couleurs"
          onClick={retirerCouleurs}
        >
          <Eraser className="h-4 w-4" />
        </ToolbarButton>

        <div className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground transition-opacity">
          {saveStatus === "saving" && <><Loader2 className="h-3 w-3 animate-spin" /> {t("admin.editor.saving")}</>}
          {saveStatus === "saved" && <><Check className="h-3 w-3 text-verified" /> {t("admin.editor.saved")}</>}
        </div>
      </div>
      <EditorContent
        editor={editor}
        className="prose prose-neutral prose-sm max-w-none px-4 py-3 focus:outline-none min-h-[200px] [&_.ProseMirror]:outline-none"
      />
    </div>
  );
}
